import { SQL_READER_URL, SQL_WRITER_URL, TURNOVER_WRITER_URL } from "../config/api";
// Entra bearer header (empty {} when no token) — shared helper in apiToken.js.
import { authHeader } from "./apiToken";

// ── SQL string sanitizers (single source of truth — do not re-implement locally) ──
//
// clean()     — for values interpolated directly inside a '...' SQL literal
//               (inline proc params). Strips apostrophes (they'd break the
//               literal), normalizes µ/° to ASCII, drops other non-ASCII.
// sqlEscape() — for a JSON payload string embedded in a SQL literal
//               (`@payload = '...'`). Doubles apostrophes (T-SQL's escape) so
//               content like "client's" survives intact. Prefer this for any
//               free-text that must round-trip losslessly.
export function clean(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/µ/g, "u")    // micro sign → u (e.g. 0.3µm → 0.3um)
    .replace(/°/g, " deg") // degree sign
    .replace(/'/g, "")     // apostrophes break '...' literals
    .replace(/[^\x00-\x7F]/g, "");
}

export function sqlEscape(v) {
  if (v === null || v === undefined) return "";
  return String(v).replace(/'/g, "''");
}

// Escapes a JSON payload for embedding in a SQL '...' literal (`@payload = '...'`).
export const payloadOf = (obj) => sqlEscape(JSON.stringify(obj));

// Retry once on 500 (Azure Function cold start can fail the first request).
// On non-500 errors throw immediately — those are bad queries, not transients.
async function sqlFetch(sql, params) {
  const auth = await authHeader();
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await fetch(SQL_READER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify(params ? { query: sql, params } : { query: sql }),
    });
    if (res.ok) return res;
    if (res.status !== 500 || attempt === 1) {
      // Read the body ONCE as text, then try to parse it — res.clone() isn't
      // universally available and double-reading a consumed body throws.
      let detail = `SQL reader error ${res.status}`;
      try {
        const raw = await res.text();
        try {
          // The function returns { error: "<sql message>" } (error is a STRING),
          // so surface that first — otherwise the real cause is lost.
          const body = JSON.parse(raw);
          detail = (typeof body?.error === "string" && body.error)
            || body?.message || body?.error?.message || body?.detail || detail;
        } catch {
          if (raw && raw.trim()) detail = raw;
        }
      } catch {}
      throw new Error(detail);
    }
    await new Promise(r => setTimeout(r, 1500));
  }
}

// Returns the full ResultSets object — use when a stored proc returns multiple tables.
// Pass `params` to bind large/awkward values out-of-band (e.g. `@payload = @payload`)
// instead of inlining them into the statement — required for multi-MB blobs.
export async function sqlReadRaw(sql, params) {
  const res = await sqlFetch(sql, params);
  const data = await res.json();
  return data?.ResultSets ?? data;
}

export async function sqlRead(sql, params) {
  const res = await sqlFetch(sql, params);
  const data = await res.json();
  return data?.ResultSets?.Table1 ?? data?.Table1 ?? data?.value ?? [];
}

// Used by turnover-workflow pages (Help, Turnovers, TurnoverDetail) which
// route writes through the dedicated turnover-write function endpoint.
// Optional `params` binds large values out-of-band (`@payload = @payload`),
// exactly like sqlRead — turnover-write's handler types every string param
// NVARCHAR(MAX) the same way sql-read does (Help's screenshot attachments).
export async function turnoverWrite(sql, params) {
  const auth = await authHeader();
  const res = await fetch(TURNOVER_WRITER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth },
    body: JSON.stringify(params ? { query: sql, params } : { query: sql }),
  });
  if (!res.ok) {
    let detail = `Turnover write error ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.message || body?.error?.message || detail;
    } catch {
      try { detail = (await res.text()) || detail; } catch {}
    }
    throw new Error(detail);
  }
  return res.json().catch(() => ({ success: true }));
}

export async function sqlWrite(payload) {
  const auth = await authHeader();
  const res = await fetch(SQL_WRITER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`SQL writer error ${res.status}`);
  return res.json().catch(() => ({ success: true }));
}
